import { useRef, useState } from 'react';
import { tss } from 'tss-react/mui';
import { FormattedMessage } from 'react-intl';
import { graphql, QueryRenderer, useFragment } from 'react-relay';
import { Button, styled } from '@mui/material';
import { getRelayEnvironment } from '@/RelayEnvironment';
import type { Project } from '../../schema.graphql';
import { insidePaperFullWidthStyles, paperPaddingStyles } from '../Paper';
import MetaSelect from './MetaSelect';
import SelectedRespondentFilters from './SelectedRespondentFilters';
import { RespondentFilterFooter } from './RespondentFilterFooter';
import type {
    ResultFilter,
    RespondentFilterUsingMetaResultsQuery,
    RespondentFilterUsingMetaResultsQuery$data,
} from './__generated__/RespondentFilterUsingMetaResultsQuery.graphql';
import type { RespondentFilterUsingMetaResults_questionnaire$key } from './__generated__/RespondentFilterUsingMetaResults_questionnaire.graphql';

const FilterContainer = styled('div')(({ theme }) => ({
    backgroundColor: theme.bino.primaryColor,
    border: 'none',
    boxSizing: 'border-box',
    position: 'relative',
    ...insidePaperFullWidthStyles(theme),
    ...paperPaddingStyles(theme),
}));
const useStyles = tss.create(({ theme }) => ({
    root: {
        minHeight: 40,
    },
    filterRespondentsButton: {
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            width: 'auto',
            float: 'right',
        },
    },
}));

const respondentFilterQuery = graphql`
    query RespondentFilterUsingMetaResultsQuery(
        $questionnaireId: ID!
        $filters: [ResultFilter!]
    ) {
        metaResults: metaResults(
            questionnaireId: $questionnaireId
            filters: $filters
        ) {
            metaValue
            metaKey
            responseCount
        }
    }
`;

export type RespondentFilterUsingMetaResultsProps = {
    visible?: boolean;
    filters?: ReadonlyArray<ResultFilter>;
    questionnaire: RespondentFilterUsingMetaResults_questionnaire$key;
    projects: ReadonlyArray<Project>;
    metaResults: RespondentFilterUsingMetaResultsQuery$data['metaResults'];

    onChange(filters: ReadonlyArray<ResultFilter>): void;
};

type MetaResults =
    RespondentFilterUsingMetaResultsQuery['response']['metaResults'];

export const RespondentFilterUsingMetaResults = (
    props: RespondentFilterUsingMetaResultsProps,
) => {
    const [visible, setVisible] = useState(!!props.visible);
    const [filters, setFilters] = useState<ReadonlyArray<ResultFilter>>(
        props.filters?.filter ? props.filters.filter((f) => f !== null) : [],
    );
    const cachedMetaResults = useRef<MetaResults>();

    const clearFilter = () => {
        setFilters([]);
        props.onChange([]);
    };

    const applyFilter = () => {
        props.onChange(filters);
    };

    const onChange = (
        metaKey: string,
        hasMetaValues: ReadonlyArray<string>,
    ) => {
        let hasFilter = false;
        const metaValues = hasMetaValues.filter((metaValue) =>
            Boolean(metaValue),
        );
        const newFilters = filters.map((filter) => {
            if (filter.metaKey === metaKey) {
                hasFilter = true;
                return {
                    metaKey,
                    metaValues,
                };
            }
            return filter;
        });

        if (!hasFilter) {
            newFilters.push({ metaKey, metaValues });
        }
        setFilters(newFilters);
    };

    const { projects } = props;
    const questionnaire = useFragment(
        fragments.questionnaire,
        props.questionnaire,
    );
    const { classes } = useStyles();

    const renderFilters = (metaResults: MetaResults, loading: boolean) => {
        const totalRespondents =
            metaResults
                .filter(
                    ({ metaKey, metaValue }) =>
                        metaKey === 'Questionnaire' &&
                        metaValue === questionnaire.originalId,
                )
                .map(({ responseCount }) => responseCount)
                .pop() || 0;

        const metaList = questionnaire.questions
            .filter(({ isMeta }) => isMeta)
            .map((question) => ({
                label: question.shortForm,
                options: questionnaire.responseScales
                    .find(
                        ({ originalId }) =>
                            originalId === question.responseScaleId,
                    )
                    .responseOptions.map(({ label, value }) => ({
                        label: label,
                        value: value.toString(), // graphql wants strings for metaValue
                    })),
                metaKey: question.originalId,
                metaValues:
                    filters
                        .filter(
                            ({ metaKey }) => metaKey === question.originalId,
                        )
                        .map(({ metaValues }) => metaValues)
                        .pop() || [],
            }));

        if (!visible) {
            return (
                <div className={classes.root}>
                    <Button
                        color="secondary"
                        className={classes.filterRespondentsButton}
                        onClick={() => setVisible(true)}
                    >
                        <FormattedMessage
                            defaultMessage="Filter respondents"
                            description="Filter Respondents Button"
                        />
                    </Button>
                    <SelectedRespondentFilters
                        metaList={metaList}
                        metaResults={metaResults}
                        filterMode="LogicalAnd"
                    />
                </div>
            );
        }

        return (
            <div className={classes.root}>
                <FilterContainer>
                    {metaList.map((meta) => (
                        <MetaSelect
                            key={meta.metaKey}
                            onChange={onChange}
                            questionnaireId={questionnaire.originalId}
                            filters={filters}
                            metaResults={metaResults}
                            loadingMetaResults={loading} // metaResults={ metaResults }
                            {...meta}
                        />
                    ))}
                    <RespondentFilterFooter
                        loading={loading}
                        respondentCount={totalRespondents}
                        projectCount={projects?.length}
                        onFilter={applyFilter}
                        onClear={clearFilter}
                    />
                </FilterContainer>
            </div>
        );
    };
    if (props.metaResults) {
        return renderFilters(props.metaResults, false);
    }
    return (
        <QueryRenderer<RespondentFilterUsingMetaResultsQuery>
            query={respondentFilterQuery}
            environment={getRelayEnvironment()}
            variables={{
                questionnaireId: questionnaire.originalId,
                filters,
            }}
            render={({ error, props }) => {
                if (error) {
                    return false;
                }
                const metaResults =
                    props?.metaResults || cachedMetaResults.current || [];

                cachedMetaResults.current = metaResults;

                return renderFilters(metaResults, !props);
            }}
        />
    );
};

const fragments = {
    questionnaire: graphql`
        fragment RespondentFilterUsingMetaResults_questionnaire on QuestionnaireInterface {
            originalId
            questions {
                originalId
                isMeta
                shortForm
                responseScaleId: originalResponseScaleId
            }
            responseScales {
                originalId
                responseOptions {
                    value
                    label
                }
            }
        }
    `,
};

export default RespondentFilterUsingMetaResults;
